import React, { useState, useEffect } from "react";
import { Tab, Nav, Row, Col, Form, Container, InputGroup } from "react-bootstrap";

const rewardData = [
    {
        tab: "Base / Gross Salary",
        fields: ["Basic pay"],
        hasCurrencySelector: true
    },
    {
        tab: "Structured Allowances and Benefits",
        fields: ["Travel allowance", "xxxxx"]
    },
    {
        tab: "Other Allowances and Benefits",
        sections: [
            {
                title: "Obligatory / Mandatory",
                fields: ["Medical aid", "Provident fund", "Risk benefits", "xxxxx"]
            },
            {
                title: "Non-obligatory / Elective",
                fields: ["Funeral cover", "xxxxxx"]
            },
            {
                title: "Supplementary Allowances and Benefits",
                fields: ["Canteen subsidy", "Lifestyle benefits", "Cellphone allowance", "Per diem", "xxxxx"]
            },
            {
                title: "Other Supplementary Benefits",
                fields: ["Data", "Airtime", "xxxxx"]
            }
        ]
    },
    {
        tab: "Variable Pay",
        sections: [
            {
                title: "Supplementary Payments",
                fields: ["Standby allowance", "Overtime pay", "Callout payment", "Shift allowance", "xxxxx"]
            },
            {
                title: "Incentives",
                fields: [
                    "Notional share options",
                    "Performance share plan",
                    "Performance bonus plan",
                    "Sales commission scheme",
                    "Sales bonus",
                    "Sign-on bonus",
                    "Retention bonus",
                    "ESOP",
                    "Group Fintech LTI scheme",
                    "Other incentive schemes",
                    "xxxxx"
                ]
            }
        ]
    },
    {
        tab: "Reimbursements",
        fields: ["Payment 1", "Payment 1"]
    }
];

const currencyRates = {
    EUR: { ZAR: 20, USD: 1.1 },
    GBP: { ZAR: 23, USD: 1.3 },
    ZAR: { ZAR: 1, USD: 0.055 },
    USD: { ZAR: 18, USD: 1 },
    CDF: { ZAR: 0.009, USD: 0.00045 },
    XOF: { ZAR: 0.03, USD: 0.0016 },
    XAF: { ZAR: 0.03, USD: 0.0016 },
    GHS: { ZAR: 1.6, USD: 0.084 },
    NGN: { ZAR: 0.012, USD: 0.00067 },
    SSP: { ZAR: 0.14, USD: 0.0078 }
};

function formatAccounting(value) {
    if (value === "" || isNaN(value)) return "";
    return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function RewardsTabbedForm({ value = {}, onChange }) {
    const [formData, setFormData] = useState(value || {});
    const [localCurrency, setLocalCurrency] = useState(
        value["Base / Gross Salary"]?.["Local Currency"] || "ZAR"
    );
    const [focusedField, setFocusedField] = useState(null);

    useEffect(() => {
        setFormData(value || {});
        setLocalCurrency(value["Base / Gross Salary"]?.["Local Currency"] || "ZAR");
    }, [value]);

    const handleChange = (tab, field, currency, rawValue) => {
        const numericValue = rawValue.replace(/,/g, "");
        if (!/^\d*(\.\d{0,2})?$/.test(numericValue)) return;
        const updated = {
            ...formData,
            [tab]: {
                ...formData[tab],
                [field]: {
                    ...formData[tab]?.[field],
                    [currency]: numericValue
                }
            }
        };
        setFormData(updated);
        if (onChange) onChange(updated);
    };

    const handleCurrencyChange = e => {
        const newCurrency = e.target.value;
        setLocalCurrency(newCurrency);
        const updated = {
            ...formData,
            "Base / Gross Salary": {
                ...formData["Base / Gross Salary"],
                "Local Currency": newCurrency
            }
        };
        setFormData(updated);
        if (onChange) onChange(updated);
    };

    const renderFieldInputs = (tab, field) => {
        const conversion = currencyRates[localCurrency] || { ZAR: 0, USD: 0 };
        const localAmountRaw = formData[tab]?.[field]?.["Local"] || "";
        const localAmount = parseFloat(localAmountRaw);
        const convertedZAR = localAmount ? (localAmount * conversion.ZAR).toFixed(2) : "";
        const convertedUSD = localAmount ? (localAmount * conversion.USD).toFixed(2) : "";
        const isFocused = focusedField === `${tab}_${field}`;

        return (
            <Form.Group className="mb-3" key={field}>
                <Form.Label><strong>{field}</strong></Form.Label>
                <Row>
                    <Col md={4}>
                        <InputGroup>
                            <InputGroup.Text>{localCurrency}</InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Local amount"
                                value={isFocused ? localAmountRaw : formatAccounting(localAmountRaw)}
                                onFocus={() => setFocusedField(`${tab}_${field}`)}
                                onBlur={() => setFocusedField(null)}
                                onChange={e => handleChange(tab, field, "Local", e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={4}>
                        <InputGroup>
                            <InputGroup.Text>ZAR</InputGroup.Text>
                            <Form.Control
                                type="text"
                                value={formatAccounting(convertedZAR)}
                                placeholder="ZAR amount"
                                readOnly
                            />
                        </InputGroup>
                    </Col>
                    <Col md={4}>
                        <InputGroup>
                            <InputGroup.Text>USD</InputGroup.Text>
                            <Form.Control
                                type="text"
                                value={formatAccounting(convertedUSD)}
                                placeholder="USD amount"
                                readOnly
                            />
                        </InputGroup>
                    </Col>
                </Row>
            </Form.Group>
        );
    };

    return (
        <Container fluid className="p-4">
            <h3 className="mb-4">Employee Rewards Form</h3>
            <Tab.Container defaultActiveKey={rewardData[0].tab}>
                <Row>
                    <Col sm={3}>
                        <Nav variant="pills" className="flex-column">
                            {rewardData.map((tab, idx) => (
                                <Nav.Item key={idx}>
                                    <Nav.Link eventKey={tab.tab}>{tab.tab}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Col>
                    <Col sm={9}>
                        <Tab.Content>
                            {rewardData.map((tab, idx) => (
                                <Tab.Pane eventKey={tab.tab} key={idx}>
                                    {tab.hasCurrencySelector && (
                                        <Form.Group className="mb-4">
                                            <Form.Label><strong>Select Local Currency</strong></Form.Label>
                                            <Form.Select
                                                value={localCurrency}
                                                onChange={handleCurrencyChange}
                                            >
                                                <option value="ZAR">ZAR</option>
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                                <option value="GBP">GBP</option>
                                                <option value="CDF">Congo (CDF)</option>
                                                <option value="XOF">Cote D’Ivoire (XOF)</option>
                                                <option value="XAF">Cameroon (XAF)</option>
                                                <option value="GHS">Ghana (GHS)</option>
                                                <option value="NGN">Nigeria (NGN)</option>
                                                <option value="SSP">South Sudan (SSP)</option>
                                            </Form.Select>
                                        </Form.Group>
                                    )}
                                    {tab.sections ? (
                                        tab.sections.map((section, sIdx) => (
                                            <div key={sIdx} className="mb-4">
                                                <h5 className="text-primary mb-3">{section.title}</h5>
                                                {section.fields.map(field => renderFieldInputs(tab.tab, field))}
                                            </div>
                                        ))
                                    ) : (
                                        tab.fields.map(field => renderFieldInputs(tab.tab, field))
                                    )}
                                </Tab.Pane>
                            ))}
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </Container>
    );
}

export default RewardsTabbedForm;